/**
 * Unified API client — combines frontier worker, envoy worker, and GRAYMANTLE control-server.
 *
 * All calls use same-origin with credentials. The JWT from portal-session is attached
 * as Bearer token.
 */

import { frontierApi, envoyApi, controlApi } from "./api-base";
import { getJWT, clearJWT } from "./portal-session";
import { membraneObject } from "./membrane-language";

// ── Frontier Client (F0-F13 pipeline) ──────────────────────────────────────

export class FrontierClient {
  private token: string;

  constructor(token: string) {
    this.token = token;
  }

  private async req<T>(method: string, path: string, body?: any): Promise<T> {
    const res = await fetch(frontierApi(path), {
      method,
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401) { clearJWT(); throw new Error("Session expired"); }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // Runs
  async listRuns(limit?: number) {
    const qs = limit ? `?limit=${limit}` : "";
    return this.req("GET", `runs${qs}`);
  }
  async createRun(intent: string, autonomyLevel: string) {
    return this.req("POST", "runs", { operator_intent: intent, autonomy_level: autonomyLevel });
  }
  async getRun(id: string) { return this.req("GET", `runs/${id}`); }

  // Build / Greenfielder
  async createBuild(input: { source_url: string; intent: string; target?: string; execution_mode?: "DFY" | "STEP" }) {
    return this.req("POST", "builds", input);
  }
  async getBuild(id: string) { return this.req("GET", `builds/${id}`); }
  async advanceBuild(id: string) { return this.req("POST", `builds/${id}/advance`); }

  // F-stage operations
  async runInquiry(runId: string, transcript?: string) {
    return this.req("POST", `runs/${runId}/inquiry`, transcript ? { session_transcript: transcript } : undefined);
  }
  async chatInquiry(runId: string, messages: Array<{ role: string; content: string }>) {
    return this.req<{ reply: string; run_id: string }>("POST", `runs/${runId}/inquiry/chat`, { messages });
  }
  async runThesis(runId: string) { return this.req("POST", `runs/${runId}/thesis`); }
  async compileGreenfield(runId: string) { return this.req("POST", `runs/${runId}/greenfield`); }
  async getGreenfield(runId: string) { return this.req("GET", `runs/${runId}/greenfield`); }
  async createSpec(runId: string) { return this.req("POST", `runs/${runId}/spec`); }
  async submitApproval(runId: string, approved: boolean, note?: string) {
    return this.req("POST", `runs/${runId}/approval`, { approved, note });
  }
  async runWriteback(runId: string) { return this.req("POST", `runs/${runId}/writeback`); }
  async getLineage(runId: string) { return this.req("GET", `runs/${runId}/lineage`); }

  // Ship state
  async listShipState(params?: any) {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return this.req("GET", `ship-state${qs}`);
  }
}

// ── Envoy Client (campaigns, sequences, contacts) ──────────────────────────

export class EnvoyClient {
  private token: string;

  constructor(token: string) {
    this.token = token;
  }

  private async req<T>(method: string, path: string, body?: any): Promise<T> {
    const res = await fetch(envoyApi(path), {
      method,
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401) { clearJWT(); throw new Error("Session expired"); }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // Agent
  getAgentConfig() { return this.req("GET", "/agent/config"); }
  getAgentStatus() { return this.req("GET", "/agent/status"); }
  getAgentContext() { return this.req("GET", "/agent/context"); }
  getCronStatus() { return this.req("GET", "/cron/status"); }
  // Notion
  getNotionDatabases() { return this.req("GET", "/notion/databases"); }

  // Contacts
  listContacts(params?: any) {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return this.req("GET", `/contacts${qs}`);
  }
  createContact(data: any) { return this.req("POST", "/contacts", data); }

  // Campaigns
  listCampaigns() { return this.req("GET", "/campaigns"); }
  createCampaign(data: any) { return this.req("POST", "/campaigns", data); }

  // Sequences
  listSequences() { return this.req("GET", "/sequences"); }
  createSequence(data: any) { return this.req("POST", "/sequences", data); }
  enrollContacts(seqId: string, contactIds: string[]) {
    return this.req("POST", `/sequences/${seqId}/enroll`, { contact_ids: contactIds });
  }

  // Threads / Inbox
  listThreads(params?: any) {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return this.req("GET", `/threads${qs}`);
  }
  getThreadMessages(id: string) { return this.req("GET", `/threads/${id}/messages`); }

  // Billing
  getBilling() { return this.req("GET", "/billing"); }
  getCredits() { return this.req("GET", "/billing/credits"); }
  listApiKeys() { return this.req("GET", "/billing/keys"); }
  createApiKey(name: string) { return this.req("POST", "/billing/keys", { name }); }

  // Onboarding
  getOnboarding() { return this.req("GET", "/onboarding"); }
  updateOnboarding(step: string, data: any) { return this.req("PATCH", "/onboarding", { step, data }); }

  // Escalations
  listEscalations(params?: any) {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return this.req("GET", `/escalations${qs}`);
  }
}

// ── Control Server Client (Register, Conveyance, Operator) ─────────────────

export class ControlClient {
  private token: string;

  constructor(token: string) {
    this.token = token;
  }

  private async req<T>(method: string, path: string, body?: any): Promise<T> {
    const res = await fetch(controlApi(path), {
      method,
      headers: {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401) { clearJWT(); throw new Error("Session expired"); }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // Register (F10/F11)
  getRegister() { return this.req("GET", "register"); }
  getRegisterObjects() { return this.req("GET", "register/objects"); }
  getCausalSpine() { return this.req("GET", "register/causal-spine"); }

  // Agent chat (F1-F4)
  getChatHistory(tenantId?: string) {
    const qs = tenantId ? `?tenant_id=${tenantId}` : "";
    return this.req("GET", `agent/chat/history${qs}`);
  }
  postChat(message: string) { return this.req("POST", "agent/chat", { message }); }

  // Agent status (F9)
  getAgentStatus() { return this.req("GET", "agent/status"); }

  // Offers (F5)
  listOffers() { return this.req("GET", "offers"); }
  createOffer(data: any) { return this.req("POST", "offers", data); }

  // Conveyance (F13A)
  generateConveyance(data: { tenant_id: string; package_type?: string }) {
    return this.req("POST", "conveyance", data);
  }

  // Profile (F13B)
  getProfile() { return this.req("GET", "profile"); }
  updateProfile(data: any) { return this.req("PUT", "profile", data); }

  // Settings (F12)
  getSettings() { return this.req("GET", "settings"); }
  updateSettings(data: any) { return this.req("PUT", "settings", data); }

  // Operator (KB's view)
  getFleet() { return this.req("GET", "operator/fleet"); }
  createOperatorCampaign(data: any) { return this.req("POST", "operator/campaign", data); }

  // Hermes / F3 / F4 (augmentations)
  getHermesStatus() { return this.req("GET", "hermes/status"); }
  getHermesF4Verdicts() { return this.req("GET", "hermes/f4-verdicts"); }
  getHermesF3Packet() { return this.req("GET", "hermes/f3-packet"); }
  getAgentGovernor() { return this.req("GET", "agent/governor"); }
  getAgentManifest() { return this.req("GET", "agent/manifest"); }

  // MCP (scaffold portal compatibility)
  async mcpCall(method: string, params: any = {}) {
    const body = { jsonrpc: "2.0", id: Date.now() + Math.random(), method, params };
    const res = await fetch("/mcp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (json.error) throw new Error(json.error.message || json.error.code || "MCP error");
    return json.result;
  }
  async mcpToolCall(name: string, args: any = {}) {
    return this.mcpCall("tools/call", { name, arguments: args });
  }
}

// ── Factory ────────────────────────────────────────────────────────────────

export function getClients() {
  const token = getJWT();
  if (!token) return null;
  return {
    frontier: new FrontierClient(token),
    envoy: new EnvoyClient(token),
    control: new ControlClient(token),
  };
}

export function applyMembrane<T>(data: T, isOperator: boolean): T {
  return membraneObject(data, isOperator);
}
